import React, { useState } from 'react';

interface ImageUploadProps {
    id: string;
    name: string;
    label: string;
    accept?: string;
    onFileChange?: (file: File | null) => void;
    value?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ id, name, label, accept = "image/*,application/pdf", onFileChange, value }) => {
    const [file, setFile] = useState<File | null>(null);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0] || null;
        if (selectedFile) {
            setFile(selectedFile);
            setFileUrl(URL.createObjectURL(selectedFile));
            onFileChange && onFileChange(selectedFile);
        } else {
            setFile(null);
            setFileUrl(null);
            onFileChange && onFileChange(null);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFileUrl(null);
        onFileChange && onFileChange(null);
    };

    const isImageFile = (file: File | null) => {
        return file?.type.startsWith('image/');
    };

    return (
        <div className="relative">
            {(value || file) && (
                <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="absolute top-2 right-2 bg-white p-1 rounded-full shadow-md z-10 hover:bg-primaryColor hover:text-secondaryColor transition-colors transform duration-200 focus:outline-none"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-4 h-4"
                    >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            )}
            <label htmlFor={id}>
                <input
                    type="file"
                    id={id}
                    name={name}
                    accept={accept}
                    onChange={handleInputChange}
                    hidden
                />
                {value && (
                    <div className="aspect-video bg-gray-200 w-full h-full flex flex-col justify-center items-center gap-5 cursor-pointer rounded-md overflow-hidden relative">
                        <img
                            src={value}
                            alt={label}
                            className="h-full w-full object-cover"
                        />
                    </div>
                )}
                {file ? (
                    <div className="aspect-video bg-gray-200 w-full h-full flex flex-col justify-center items-center gap-5 cursor-pointer rounded-md overflow-hidden relative">
                        <div className="bg-gray-200 flex items-center justify-center">
                            {isImageFile(file) ? (
                                <img
                                    src={fileUrl || ''}
                                    alt={label}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex flex-col items-center">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        className="h-8 w-8 text-gray-500"
                                        fill="currentColor"
                                    >
                                        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 14H8v-2h8v2zm0-4H8v-2h8v2zm-3-5.5V9h5.5L13 4.5z" />
                                    </svg>
                                    <span>{file.name}</span>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    value ? null :
                        <div className="aspect-video bg-gray-200 w-full h-full flex flex-col justify-center items-center gap-5 cursor-pointer rounded-md p-2">
                            <div className="bg-gray-200 flex items-center justify-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 37 36"
                                    className="h-8 w-8 text-white"
                                    fill="currentColor"
                                >
                                    <path
                                        d="M35.9939 14.9112L35.9845 14.8737L30.7252 1.50962C30.4908 0.754931 29.7923 0.234619 29.0002 0.234619H7.68141C6.88453 0.234619 6.17672 0.764307 5.95172 1.52837L1.03453 14.7565L1.02047 14.7893L1.01109 14.8268C0.950156 15.0565 0.931406 15.2909 0.964219 15.5206C0.959531 15.5956 0.954844 15.6706 0.954844 15.7456V32.8971C0.956083 33.6526 1.25675 34.3768 1.79096 34.911C2.32517 35.4452 3.04936 35.7459 3.80484 35.7471H33.2048C34.7752 35.7471 36.0548 34.4674 36.0595 32.8971V15.7456C36.0595 15.6846 36.0595 15.6237 36.0548 15.5721C36.0736 15.3424 36.0548 15.1221 35.9939 14.9112ZM22.1283 12.8956L22.1142 13.6315C22.0767 15.7362 20.6236 17.1518 18.5002 17.1518C17.4642 17.1518 16.5736 16.819 15.9314 16.1862C15.2892 15.5534 14.9377 14.6721 14.9189 13.6315L14.9048 12.8956H5.25797L8.98453 3.83462H27.697L31.5267 12.8956H22.1283ZM4.55016 16.4956H11.9236C13.0627 19.1721 15.4861 20.7518 18.5048 20.7518C20.0845 20.7518 21.5517 20.3112 22.7377 19.4768C23.7783 18.7456 24.5892 17.7237 25.1142 16.4956H32.4502V32.1471H4.55016V16.4956Z"
                                    />
                                </svg>
                            </div>
                            <span className="text-xs text-center">
                                {label}
                            </span>
                            <span className="text-xs text-center">
                                Support for a single or bulk upload. Strictly prohibit from uploading company data or other banned files.
                            </span>
                        </div>
                )}
            </label>
        </div>
    );
};

export default ImageUpload;
